import TextEditor from '../../../components/TextEditor';
import { FormControlLabel, FormControl, InputLabel, Select, MenuItem, Stack, Switch, TextField } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ClientProvider from '../../../modules/ClientProvider';
import { IProject, ISurveyLink } from '../../../types';

interface SurveyLinkFormData extends Partial<ISurveyLink> {
    touchpoint_uuid?: string;
}

interface SurveyLinkFormProps {
    surveyLink?: ISurveyLink | null;
    onSubmit: (data: ISurveyLink) => void;
    project?: IProject;
}

export const SurveyLinkForm = ({ surveyLink = null, onSubmit, project }: SurveyLinkFormProps) => {
    const [data, setData] = useState<SurveyLinkFormData>(() => ({
        ...surveyLink,
        touchpoint_uuid: surveyLink?.touchpoint?.uuid || ''
    }));
    const [touchpoints, setTouchpoints] = useState<any[]>([]);
    const [touchpointsLoading, setTouchpointsLoading] = useState(false);
    const { getAccessTokenSilently } = useAuth0();

    useEffect(() => {
        setData({
            ...surveyLink,
            touchpoint_uuid: surveyLink?.touchpoint?.uuid || ''
        });
    }, [surveyLink]);

    useEffect(() => {
        const fetchTouchpoints = async () => {
            if (!project?.id) return;
            
            setTouchpointsLoading(true);
            try {
                const accessToken = await getAccessTokenSilently();
                const adminTouchpointClient = ClientProvider.provideAdminTouchpointClient();
                const { data: response, error } = await adminTouchpointClient.getTouchpointsByProjectId(accessToken, project.id);
                
                if (response?.touchpoints) {
                    setTouchpoints(response.touchpoints);
                }
            } catch (error) {
                console.error('Error fetching touchpoints:', error);
            } finally {
                setTouchpointsLoading(false);
            }
        };

        fetchTouchpoints();
    }, [project?.id, getAccessTokenSilently]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.type === "checkbox" ? event.target.checked : event.target.value;
        setData(prevData => ({
            ...prevData,
            [event.target.name]: newValue,
        }));
    };

    const handleSelectChange = (event: any) => {
        setData(prevData => ({
            ...prevData,
            [event.target.name]: event.target.value,
        }));
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        onSubmit(data as ISurveyLink);
    };

    return (
        <Stack
            sx={{
                width: '50ch',
            }}
            spacing={2}
        >
            <TextField name="name" label="Name" value={data.name || ''} onChange={handleChange} />
            
            <FormControl fullWidth>
                <InputLabel id="touchpoint-select-label">Touchpoint</InputLabel>
                <Select
                    labelId="touchpoint-select-label"
                    name="touchpoint_uuid"
                    value={data.touchpoint_uuid || ''}
                    label="Touchpoint"
                    onChange={handleSelectChange}
                    disabled={touchpointsLoading}
                >
                    <MenuItem value="">
                        <em>Select a touchpoint</em>
                    </MenuItem>
                    {touchpoints.map((touchpoint) => (
                        <MenuItem key={touchpoint.uuid} value={touchpoint.uuid}>
                            {touchpoint.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            
            <FormControlLabel name="is_active" control={<Switch checked={data.is_active || false} onChange={handleChange} />} label="Is Active" />
            <FormControlLabel name="enable_email" control={<Switch checked={data.enable_email || false} onChange={handleChange} />} label="Enable Email" />
            <br/>
            <TextField name="reward_description" label="Reward Description" value={data.reward_description || ''} onChange={handleChange} />
            <TextField name="duration_description" label="Duration Description" value={data.duration_description || ''} onChange={handleChange} />
            <br/>
            <TextField name="partner_name" label="Partner Name" value={data.partner_name || ''} onChange={handleChange} />
            <TextField name="survey_url" label="Survey URL" value={data.survey_url || ''} onChange={handleChange} />
            <TextField name="link_params" label="Survey URL Params" value={data.link_params || ''} onChange={handleChange} />
            <br/>
            <FormControlLabel name="passthrough" control={<Switch checked={data.passthrough || false} onChange={handleChange} />} label="Passthrough" />
            <TextField name="complete_redirect_url" label="Redirect URL: Completed" value={data.complete_redirect_url || ''} onChange={handleChange} />
            <TextField name="screen_redirect_url" label="Redirect URL: Screen Out" value={data.screen_redirect_url || ''} onChange={handleChange} />
            <TextField name="quota_redirect_url" label="Redirect URL: Quote Reached" value={data.quota_redirect_url || ''} onChange={handleChange} />
            <TextField name="quality_redirect_url" label="Redirect URL: Quality" value={data.quality_redirect_url || ''} onChange={handleChange} />
            <br/>
            <p className="bold">Custom Overview Text</p>
            <div className="display-box white">
                <TextEditor content={data.overview_text || ''} id="overview_text" form={data} setForm={setData} />
            </div>

            <button type="submit" onClick={handleSubmit}>Submit</button>
        </Stack>
    );
};